import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useCreateCourse, useUpdateCourse } from "./hooks";
import {
  coursesAPI,
  Course,
  CourseLesson,
  CourseSection,
  SectionSchedule,
  CourseLevel,
  CourseStatus,
  LessonSource,
  LessonType,
  LessonResource,
  LessonSettings,
  getThumbnailUrl,
} from "./api";
import { useT } from "../../i18n";

const categories = ["Foundation", "Advanced", "Integration", "Sales", "Certification"];
const levels: CourseLevel[] = ["beginner", "intermediate", "advanced", "certification"];
const sources: LessonSource[] = ["youtube", "vimeo", "zoom", "iframe"];

const newId = () =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `l_${Math.floor(performance.now())}_${Math.round(performance.timeOrigin)}`;

const input =
  "w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none transition-all text-gray-900 placeholder-gray-400";

const STEPS = ["generalInfo", "curriculum"];

const defaultSettings = (): LessonSettings => ({
  requireCompletion: false,
});

// Backfill the new required fields on a (possibly legacy) lesson so old data loads.
const normalizeLesson = (l: any): CourseLesson => ({
  id: l?.id || newId(),
  title: l?.title || "",
  type: (l?.type as LessonType) || "video",
  description: l?.description,
  videoUrl: l?.videoUrl,
  source: l?.source || "youtube",
  duration: l?.duration,
  textBody: l?.textBody,
  questions: l?.questions,
  passPercent: l?.type === "quiz" ? l?.passPercent ?? 70 : l?.passPercent,
  resources: l?.resources || [],
  settings: {
    ...defaultSettings(),
    ...(l?.settings || {}),
  },
});

// Wrap a course's data into sections[] (client-side back-compat mirror of the
// server's normalizeCourse): use sections if present, else wrap legacy flat
// lessons into one default section.
const toSections = (
  c: any,
  t: (key: string) => string
): CourseSection[] => {
  if (Array.isArray(c?.sections) && c.sections.length > 0) {
    return c.sections.map((s: any) => ({
      id: s?.id || newId(),
      title: s?.title || t("academy.builder.untitledSection"),
      schedule: s?.schedule ?? null,
      lessons: (s?.lessons || []).map(normalizeLesson),
    }));
  }
  if (Array.isArray(c?.lessons) && c.lessons.length > 0) {
    return [
      {
        id: "legacy-default",
        title: t("academy.builder.curriculumTitle"),
        lessons: c.lessons.map(normalizeLesson),
      },
    ];
  }
  return [];
};

// Parse "mm:ss" or "hh:mm:ss" into seconds; non-parseable -> 0.
const durationToSeconds = (d?: string): number => {
  if (!d) return 0;
  const parts = d.trim().split(":").map((p) => parseInt(p, 10));
  if (parts.some((n) => isNaN(n))) return 0;
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  if (parts.length === 1) return parts[0];
  return 0;
};
const formatDuration = (totalSeconds: number, t: ReturnType<typeof useT>): string => {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.round((totalSeconds % 3600) / 60);
  if (h === 0) return t("academy.builder.durationMinutes", { m });
  return t("academy.builder.durationHoursMinutes", { h, m });
};

const lessonTypeChip: Record<LessonType, { icon: string; cls: string }> = {
  video: { icon: "fa-video", cls: "text-gray-500 bg-gray-100" },
  quiz: {
    icon: "fa-list-check",
    cls: "text-purple-600 bg-purple-50 border border-purple-200",
  },
  text: { icon: "fa-file-lines", cls: "text-blue-600 bg-blue-50 border border-blue-200" },
};

const CourseBuilder: React.FC = () => {
  const navigate = useNavigate();
  const t = useT();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;

  const createCourse = useCreateCourse();
  const updateCourse = useUpdateCourse();

  const [step, setStep] = useState(0);
  const [loadingExisting, setLoadingExisting] = useState(isEdit);
  // The course id we're working with: the route id when editing, or the id
  // returned after Step 1 creates the draft. Drives create-vs-update on save.
  const [courseId, setCourseId] = useState<string | null>(id || null);

  // Step 1
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("Foundation");
  const [level, setLevel] = useState<CourseLevel>("beginner");
  const [description, setDescription] = useState("");
  const [tags, setTags] = useState("");
  const [thumbnailUrl, setThumbnailUrl] = useState(""); // pasted public URL
  const [thumbnailKey, setThumbnailKey] = useState(""); // uploaded S3 key
  const [thumbPreviewUrl, setThumbPreviewUrl] = useState(""); // presigned preview of the key
  const [thumbUrlInput, setThumbUrlInput] = useState("");
  const [uploadingThumb, setUploadingThumb] = useState(false);

  // Step 2
  const [sections, setSections] = useState<CourseSection[]>([]);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [selected, setSelected] = useState<{ s: number; l: number } | null>(null);
  // tracks which lesson/resource slot is currently uploading
  const [uploadingRes, setUploadingRes] = useState<string | null>(null);
  // "Add Link" modal target + draft fields
  const [linkModal, setLinkModal] = useState<{ si: number; li: number } | null>(
    null
  );
  const [linkTitle, setLinkTitle] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  // whether the (single) course schedule panel is open

  // The course's current status (draft on create; set to published on Publish).
  const [status, setStatus] = useState<CourseStatus>("draft");

  const [error, setError] = useState("");

  // Load existing course when editing.
  useEffect(() => {
    if (!isEdit || !id) return;
    let active = true;
    coursesAPI
      .getCourseById(id)
      .then((res) => {
        if (!active) return;
        const c: Course = res.data;
        setTitle(c.title);
        setCategory(c.category || "Foundation");
        setLevel(c.level);
        setDescription(c.description || "");
        setTags((c.tags || []).join(", "));
        setThumbnailUrl(c.thumbnailUrl || "");
        setThumbnailKey(c.thumbnailKey || "");
        setStatus(c.status || "draft");
        setSections(toSections(c, t));
        // Resolve a presigned preview if the course has an uploaded thumbnail.
        if (c.thumbnailKey && !c.thumbnailUrl) {
          coursesAPI
            .getThumbnailUrl(c.thumbnailKey)
            .then((url) => active && setThumbPreviewUrl(url))
            .catch(() => {});
        }
      })
      .catch(() => toast.error(t("academy.builder.errors.loadFailed")))
      .finally(() => active && setLoadingExisting(false));
    return () => {
      active = false;
    };
  }, [id, isEdit]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Thumbnail ---
  const applyThumbUrl = () => {
    if (thumbUrlInput.trim()) {
      // Pasted URL takes over; clear any uploaded key.
      setThumbnailUrl(thumbUrlInput.trim());
      setThumbnailKey("");
      setThumbPreviewUrl("");
    }
  };
  const handleThumbFile = async (file: File) => {
    setUploadingThumb(true);
    try {
      const res = await coursesAPI.uploadThumbnail(file, title);
      // Store the S3 key; resolve a presigned URL just for preview.
      setThumbnailKey(res.fileKey);
      setThumbnailUrl("");
      try {
        const url = await coursesAPI.getThumbnailUrl(res.fileKey);
        setThumbPreviewUrl(url);
      } catch {
        /* preview is best-effort */
      }
    } catch (e: any) {
      toast.error(t("academy.builder.errors.thumbnailUploadFailed"));
    } finally {
      setUploadingThumb(false);
    }
  };

  // --- Sections ---
  const addSection = () =>
    setSections((p) => [
      ...p,
      { id: newId(), title: t("academy.builder.sectionNamePattern", { number: p.length + 1 }), lessons: [] },
    ]);
  const updateSectionTitle = (si: number, value: string) =>
    setSections((p) =>
      p.map((s, idx) => (idx === si ? { ...s, title: value } : s))
    );
  const updateSectionSchedule = (
    si: number,
    schedule: SectionSchedule | null
  ) =>
    setSections((p) =>
      p.map((s, idx) => (idx === si ? { ...s, schedule } : s))
    );
  const removeSection = (si: number) => {
    setSections((p) => p.filter((_, idx) => idx !== si));
    setSelected((sel) => (sel && sel.s === si ? null : sel));
  };
  const toggleCollapse = (sid: string) =>
    setCollapsed((p) => ({ ...p, [sid]: !p[sid] }));

  // --- Lessons (within a section) ---
  const newLesson = (): CourseLesson => ({
    id: newId(),
    title: "",
    type: "video",
    videoUrl: "",
    source: "youtube",
    resources: [],
    settings: defaultSettings(),
  });

  const addLesson = (si: number) =>
    setSections((p) =>
      p.map((s, idx) =>
        idx === si ? { ...s, lessons: [...s.lessons, newLesson()] } : s
      )
    );

  const updateLesson = (si: number, li: number, patch: Partial<CourseLesson>) =>
    setSections((p) =>
      p.map((s, sIdx) =>
        sIdx === si
          ? {
              ...s,
              lessons: s.lessons.map((l, lIdx) =>
                lIdx === li ? { ...l, ...patch } : l
              ),
            }
          : s
      )
    );

  const removeLesson = (si: number, li: number) => {
    setSections((p) =>
      p.map((s, sIdx) =>
        sIdx === si
          ? { ...s, lessons: s.lessons.filter((_, lIdx) => lIdx !== li) }
          : s
      )
    );
    setSelected((sel) => (sel && sel.s === si && sel.l === li ? null : sel));
  };

  const moveLesson = (si: number, li: number, dir: -1 | 1) =>
    setSections((p) =>
      p.map((s, sIdx) => {
        if (sIdx !== si) return s;
        const next = [...s.lessons];
        const j = li + dir;
        if (j < 0 || j >= next.length) return s;
        [next[li], next[j]] = [next[j], next[li]];
        return { ...s, lessons: next };
      })
    );

  // --- Resources ---
  const updateResource = (
    si: number,
    li: number,
    ri: number,
    patch: Partial<LessonResource>
  ) => {
    const lesson = sections[si].lessons[li];
    updateLesson(si, li, {
      resources: (lesson.resources || []).map((r, idx) =>
        idx === ri ? { ...r, ...patch } : r
      ),
    });
  };
  const removeResource = (si: number, li: number, ri: number) => {
    const lesson = sections[si].lessons[li];
    updateLesson(si, li, {
      resources: (lesson.resources || []).filter((_, idx) => idx !== ri),
    });
  };
  // Upload a file directly: create a resource slot, then upload into it.
  const addFileDirect = async (si: number, li: number, file: File) => {
    const lesson = sections[si].lessons[li];
    const ri = (lesson.resources || []).length;
    updateLesson(si, li, {
      resources: [
        ...(lesson.resources || []),
        { title: file.name, url: "", type: "file" },
      ],
    });
    setUploadingRes(`${si}-${li}-${ri}`);
    try {
      const res = await coursesAPI.uploadResourceFile(file, title);
      // Re-read latest lesson to patch the right slot.
      setSections((prev) =>
        prev.map((s, sIdx) =>
          sIdx !== si
            ? s
            : {
                ...s,
                lessons: s.lessons.map((l, lIdx) =>
                  lIdx !== li
                    ? l
                    : {
                        ...l,
                        resources: (l.resources || []).map((r, idx) =>
                          idx === ri
                            ? {
                                ...r,
                                url: res.fileUrl,
                                fileKey: res.fileKey,
                                title: res.fileName,
                                fileType: res.fileType,
                                fileSize: res.fileSize,
                              }
                            : r
                        ),
                      }
                ),
              }
        )
      );
    } catch (e: any) {
      toast.error(t("academy.builder.errors.fileUploadFailed"));
      // Roll back the empty slot on failure.
      removeResource(si, li, ri);
    } finally {
      setUploadingRes(null);
    }
  };

  // --- Add Link modal ---
  const openLinkModal = (si: number, li: number) => {
    setLinkModal({ si, li });
    setLinkTitle("");
    setLinkUrl("");
  };
  const saveLink = () => {
    if (!linkModal || !linkTitle.trim() || !linkUrl.trim()) return;
    const { si, li } = linkModal;
    const lesson = sections[si].lessons[li];
    updateLesson(si, li, {
      resources: [
        ...(lesson.resources || []),
        { title: linkTitle.trim(), url: linkUrl.trim(), type: "link" },
      ],
    });
    setLinkModal(null);
  };

  // --- Validation per step ---
  const validateStep1 = () => {
    if (!title.trim() || !category || !level || !description.trim()) {
      setError(t("academy.builder.errors.generalInfoRequired"));
      return false;
    }
    setError("");
    return true;
  };
  const validateStep2 = () => {
    const hasValidLesson = sections.some((s) =>
      s.lessons.some((l) => {
        if (!l.title.trim()) return false;
        if (l.type === "video") return !!(l.videoUrl && l.videoUrl.trim());
        if (l.type === "text") return !!(l.textBody && l.textBody.trim());
        if (l.type === "quiz") return (l.questions || []).length > 0;
        return false;
      })
    );
    if (!hasValidLesson) {
      setError(t("academy.builder.errors.lessonRequired"));
      return false;
    }
    setError("");
    return true;
  };

  const goBack = () => {
    if (step === 0) navigate("/academy");
    else setStep((s) => Math.max(s - 1, 0));
  };

  // Strip incomplete lessons/resources before sending.
  const buildSectionsPayload = (): CourseSection[] =>
    sections
      .map((s) => ({
        ...s,
        lessons: s.lessons
          .filter((l) => {
            if (!l.title.trim()) return false;
            if (l.type === "video") return !!(l.videoUrl && l.videoUrl.trim());
            if (l.type === "text") return !!(l.textBody && l.textBody.trim());
            if (l.type === "quiz") return (l.questions || []).length > 0;
            return false;
          })
          .map((l) => ({
            ...l,
            resources: (l.resources || []).filter(
              (r) => r.title.trim() && r.url.trim()
            ),
          })),
      }))
      .filter((s) => s.lessons.length > 0);

  const baseFields = (finalStatus: CourseStatus) => ({
    title,
    category,
    level,
    description: description || undefined,
    tags: tags
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean),
    // If an uploaded thumbnail (key) exists, persist the KEY only — never the
    // presigned thumbnailUrl (it expires). A pasted public URL is persisted as-is.
    thumbnailUrl: thumbnailKey ? undefined : thumbnailUrl || undefined,
    thumbnailKey: thumbnailKey || undefined,
    status: finalStatus,
  });

  // Step 1 → "Save & Next": persist general info as a draft (create or update),
  // capture the id, then advance to the Curriculum step.
  const saveStep1AndNext = async () => {
    if (!validateStep1()) return;
    try {
      // Keep current status when editing; new courses start as draft.
      const payload = {
        ...baseFields(status),
        // Preserve existing curriculum on update; new courses have none yet.
        sections: courseId ? buildSectionsPayload() : [],
      };
      if (courseId) {
        await updateCourse.mutateAsync({ id: courseId, data: payload });
      } else {
        const res = await createCourse.mutateAsync(payload);
        const newId = res?.data?.id;
        if (newId) setCourseId(newId);
      }
      setStep(1);
    } catch (e: any) {
      setError(t("academy.builder.errors.saveFailed"));
    }
  };

  // Step 2 → "Publish": save the curriculum and publish the course.
  const publish = async () => {
    if (!validateStep1()) {
      setStep(0);
      return;
    }
    if (!validateStep2()) return;
    try {
      const payload = {
        ...baseFields("published"),
        sections: buildSectionsPayload(),
      };
      if (courseId) {
        await updateCourse.mutateAsync({ id: courseId, data: payload });
      } else {
        await createCourse.mutateAsync(payload);
      }
      setStatus("published");
      navigate("/academy");
    } catch (e: any) {
      setError(t("academy.builder.errors.publishFailed"));
    }
  };

  const isSaving = createCourse.isPending || updateCourse.isPending;
  const firstVideo = sections
    .flatMap((s) => s.lessons)
    .find((l) => l.videoUrl);
  const thumbPreview =
    thumbnailUrl ||
    thumbPreviewUrl ||
    (firstVideo
      ? getThumbnailUrl(firstVideo.videoUrl || "", firstVideo.source || "")
      : null);

  if (loadingExisting) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-50">
        <i className="fa-solid fa-spinner fa-spin text-2xl text-gray-400"></i>
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1 h-full bg-gray-50">
      {/* Header */}
      <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-4 md:px-8 shrink-0 sticky top-0 z-10 shadow-sm">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate("/academy")}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <i className="fa-solid fa-arrow-left"></i>
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900 leading-tight">
              {t("academy.builder.title")}
            </h1>
            <p className="text-xs text-gray-500">
              {t("academy.builder.subtitle")}
            </p>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 md:p-8">
        <div className="max-w-6xl mx-auto">
          {/* Stepper */}
          <div className="mb-8 hidden sm:flex justify-center">
            <div className="flex items-start">
              {STEPS.map((label, i) => {
                const done = i < step;
                const current = i === step;
                return (
                  <React.Fragment key={label}>
                    <button
                      onClick={() => i <= step && setStep(i)}
                      className="flex flex-col items-center gap-2 px-2"
                    >
                      <div
                        className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm transition-all ${
                          done
                            ? "bg-brand-600 text-white"
                            : current
                            ? "bg-brand-600 text-white ring-4 ring-brand-50"
                            : "bg-white border-2 border-gray-300 text-gray-400"
                        }`}
                      >
                        {done ? <i className="fa-solid fa-check"></i> : i + 1}
                      </div>
                      <span
                        className={`text-xs whitespace-nowrap ${
                          current
                            ? "font-semibold text-brand-600"
                            : "font-medium text-gray-500"
                        }`}
                      >
                        {t(`academy.builder.steps.${label}`)}
                      </span>
                    </button>
                    {i < STEPS.length - 1 && (
                      <div
                        className={`w-16 md:w-24 h-0.5 mt-4 rounded-full transition-colors ${
                          i < step ? "bg-brand-600" : "bg-gray-200"
                        }`}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          {error && (
            <div className="mb-6 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-2 flex items-center gap-2">
              <i className="fa-solid fa-circle-exclamation"></i>
              {error}
            </div>
          )}

          {/* STEP 1 */}
          {step === 0 && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2 space-y-6">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <h2 className="text-lg font-bold text-gray-900 mb-1 flex items-center gap-2">
                    <i className="fa-solid fa-circle-info text-brand-500"></i>{" "}
                    {t("academy.builder.generalInfoTitle")}
                  </h2>
                  <p className="text-sm text-gray-500 mb-6">
                    {t("academy.builder.generalInfoIntro")}
                  </p>

                  <div className="space-y-5">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t("academy.builder.courseTitleLabel")} <span className="text-red-500">*</span>
                      </label>
                      <input
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        className={input}
                        placeholder={t("academy.builder.courseTitlePlaceholder")}
                      />
                      <p className="text-xs text-gray-400 mt-1">
                        {t("academy.builder.courseTitleHint")}
                      </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t("academy.builder.categoryLabel")} <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={category}
                          onChange={(e) => setCategory(e.target.value)}
                          className={input}
                        >
                          {categories.map((c) => (
                            <option key={c} value={c}>
                              {c}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t("academy.builder.levelLabel")} <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={level}
                          onChange={(e) => setLevel(e.target.value as CourseLevel)}
                          className={input}
                        >
                          {levels.map((l) => (
                            <option key={l} value={l}>
                              {l.charAt(0).toUpperCase() + l.slice(1)}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t("academy.builder.descriptionLabel")} <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        rows={6}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        className={`${input} resize-none`}
                        placeholder={t("academy.builder.descriptionPlaceholder")}
                      />
                      <p className="text-xs text-gray-400 mt-1">
                        {t("academy.builder.descriptionHint")}
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t("academy.builder.tagsLabel")}{" "}
                        <span className="text-gray-400 font-normal">
                          {t("common.optional")}
                        </span>
                      </label>
                      <input
                        value={tags}
                        onChange={(e) => setTags(e.target.value)}
                        className={input}
                        placeholder={t("academy.builder.tagsPlaceholder")}
                      />
                      <p className="text-xs text-gray-400 mt-1">
                        {t("academy.builder.tagsHint")}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Thumbnail + tips */}
              <div className="space-y-6">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-1">
                    {t("academy.builder.thumbnailLabel")}
                  </h3>
                  <p className="text-xs text-gray-500 mb-4">
                    {t("academy.builder.thumbnailHint")}
                  </p>

                  <label className="aspect-video bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center p-4 text-center hover:bg-brand-50 hover:border-brand-400 transition-colors cursor-pointer group relative overflow-hidden">
                    {thumbPreview ? (
                      <img
                        src={thumbPreview}
                        alt={t("academy.builder.thumbnailAlt")}
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                    ) : uploadingThumb ? (
                      <>
                        <i className="fa-solid fa-spinner fa-spin text-3xl text-brand-400 mb-3"></i>
                        <p className="text-sm text-gray-600">{t("common.loading.uploading")}</p>
                      </>
                    ) : (
                      <>
                        <i className="fa-regular fa-image text-4xl text-gray-300 mb-3 group-hover:text-brand-400 transition-colors"></i>
                        <p className="text-sm font-semibold text-gray-700 group-hover:text-brand-600">
                          {t("academy.builder.uploadThumbnail")}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          {t("academy.builder.dragAndDrop")}
                        </p>
                        <p className="text-xs text-gray-400 mt-0.5">
                          {t("academy.builder.thumbnailSpec")}
                        </p>
                      </>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) handleThumbFile(f);
                      }}
                    />
                  </label>

                  <div className="mt-4">
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      {t("academy.builder.pasteImageUrl")}
                    </label>
                    <div className="flex gap-2">
                      <input
                        value={thumbUrlInput}
                        onChange={(e) => setThumbUrlInput(e.target.value)}
                        className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none"
                        placeholder="https://..."
                      />
                      <button
                        onClick={applyThumbUrl}
                        className="px-3 py-2 bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded-lg text-xs font-medium text-gray-600 whitespace-nowrap"
                      >
                        {t("academy.builder.apply")}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* STEP 2: Curriculum */}
          {step === 1 && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                {/* Left: builder */}
                <div className="xl:col-span-2 space-y-4">
                  {sections.length === 0 && (
                    <div className="text-center py-10 bg-white border-2 border-dashed border-gray-200 rounded-xl">
                      <i className="fa-solid fa-layer-group text-3xl text-gray-300"></i>
                      <p className="mt-2 text-sm text-gray-500">
                        {t("academy.builder.noSections")}
                      </p>
                    </div>
                  )}

                  {sections.map((section, si) => {
                    const isCollapsed = !!collapsed[section.id];
                    const secSeconds = section.lessons.reduce(
                      (a, l) =>
                        a +
                        (l.type === "video"
                          ? durationToSeconds(l.duration)
                          : 0),
                      0
                    );
                    return (
                      <div
                        key={section.id}
                        className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden"
                      >
                        {/* Section header */}
                        <div
                          className="px-5 py-4 border-b border-brand-100 flex items-center justify-between"
                          style={{
                            background:
                              "linear-gradient(135deg, #F8F7FF 0%, #F5F3FF 100%)",
                          }}
                        >
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className="w-6 h-6 rounded bg-brand-600 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
                              {si + 1}
                            </div>
                            <div className="min-w-0">
                              <input
                                type="text"
                                value={section.title}
                                onChange={(e) =>
                                  updateSectionTitle(si, e.target.value)
                                }
                                placeholder={t("academy.builder.sectionTitlePlaceholder")}
                                className="text-sm font-bold text-gray-900 bg-transparent border-none outline-none focus:bg-white focus:px-2 focus:rounded focus:border focus:border-brand-300 transition-all w-48 md:w-64"
                              />
                              <p className="text-xs text-gray-500">
                                {t(
                                  section.lessons.length === 1
                                    ? "academy.lessonCountOne"
                                    : "academy.lessonCount",
                                  { count: section.lessons.length }
                                )}{" "}
                                &bull;{" "}
                                {formatDuration(secSeconds, t)}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {section.schedule && (
                              <span
                                className="inline-flex items-center gap-1 text-xs text-brand-700 bg-brand-50 border border-brand-200 px-2 py-0.5 rounded-full"
                                title={t("academy.builder.sectionHasSchedule")}
                              >
                                <i className="fa-regular fa-clock text-[10px]"></i>
                                {t("academy.builder.scheduled")}
                              </span>
                            )}
                            <button
                              onClick={() => removeSection(si)}
                              className="text-gray-400 hover:text-red-500 p-1.5 rounded-lg hover:bg-red-50 transition-colors text-sm"
                              title={t("academy.builder.deleteSection")}
                            >
                              <i className="fa-solid fa-trash-can"></i>
                            </button>
                            <button
                              onClick={() => toggleCollapse(section.id)}
                              className="text-gray-400 hover:text-gray-600 p-1.5 rounded-lg hover:bg-gray-100 transition-colors text-sm"
                              title={isCollapsed ? t("academy.builder.expand") : t("academy.builder.collapse")}
                            >
                              <i
                                className={`fa-solid ${
                                  isCollapsed
                                    ? "fa-chevron-down"
                                    : "fa-chevron-up"
                                }`}
                              ></i>
                            </button>
                          </div>
                        </div>

                        {!isCollapsed && (
                          <div className="p-4 space-y-3">
                            {section.lessons.map((lesson, li) => {
                              const isSelected =
                                selected?.s === si && selected?.l === li;
                              const thumb = lesson.videoUrl
                                ? getThumbnailUrl(
                                    lesson.videoUrl,
                                    lesson.source || ""
                                  )
                                : null;
                              const chip = lessonTypeChip[lesson.type];
                              const resUploadKeyPrefix = `${si}-${li}-`;
                              return (
                                <div
                                  key={lesson.id}
                                  onClick={() => setSelected({ s: si, l: li })}
                                  className={`group flex items-start gap-3 p-4 rounded-xl bg-white cursor-pointer transition-all ${
                                    isSelected
                                      ? "border-2 border-brand-300 bg-brand-50/50"
                                      : "border border-gray-200 hover:border-brand-300"
                                  }`}
                                >
                                  {/* Thumbnail / placeholder */}
                                  <div className="w-20 h-14 rounded-lg flex-shrink-0 overflow-hidden relative border border-gray-200 shadow-sm">
                                    {lesson.type === "video" && thumb ? (
                                      <>
                                        <div
                                          className="absolute inset-0 bg-cover bg-center"
                                          style={{
                                            backgroundImage: `url('${thumb}')`,
                                          }}
                                        ></div>
                                        <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                                          <div className="w-6 h-6 rounded-full bg-white/90 flex items-center justify-center">
                                            <i className="fa-solid fa-play text-gray-800 text-xs ml-0.5"></i>
                                          </div>
                                        </div>
                                      </>
                                    ) : (
                                      <div className="absolute inset-0 bg-gray-100 border border-dashed border-gray-300 flex items-center justify-center">
                                        <i
                                          className={`fa-solid ${
                                            lesson.type === "quiz"
                                              ? "fa-list-check"
                                              : lesson.type === "text"
                                              ? "fa-file-lines"
                                              : "fa-cloud-arrow-up"
                                          } text-gray-400 text-lg`}
                                        ></i>
                                      </div>
                                    )}
                                  </div>

                                  {/* Body */}
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-start justify-between gap-2">
                                      <div className="flex-1 min-w-0">
                                        <input
                                          value={lesson.title}
                                          onClick={(e) => e.stopPropagation()}
                                          onChange={(e) =>
                                            updateLesson(si, li, {
                                              title: e.target.value,
                                            })
                                          }
                                          placeholder={t("academy.builder.lessonTitlePlaceholder", {
                                            number: `${si + 1}.${li + 1}`,
                                          })}
                                          className="text-sm font-semibold text-gray-900 bg-transparent border-none outline-none focus:bg-white focus:px-2 focus:rounded focus:border focus:border-brand-300 transition-all w-full"
                                        />
                                        <input
                                          value={lesson.description || ""}
                                          onClick={(e) => e.stopPropagation()}
                                          onChange={(e) =>
                                            updateLesson(si, li, {
                                              description: e.target.value,
                                            })
                                          }
                                          placeholder={t("academy.builder.lessonDescriptionPlaceholder")}
                                          className="text-xs text-gray-500 mt-0.5 bg-transparent border-none outline-none focus:bg-white focus:px-2 focus:rounded focus:border focus:border-brand-200 transition-all w-full"
                                        />
                                      </div>
                                      <div className="flex items-center gap-1 flex-shrink-0">
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            moveLesson(si, li, -1);
                                          }}
                                          disabled={li === 0}
                                          className="text-gray-400 hover:text-brand-600 p-1.5 rounded-lg hover:bg-brand-50 transition-colors text-xs disabled:opacity-30"
                                          title={t("academy.builder.moveUp")}
                                        >
                                          <i className="fa-solid fa-arrow-up"></i>
                                        </button>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            moveLesson(si, li, 1);
                                          }}
                                          disabled={
                                            li === section.lessons.length - 1
                                          }
                                          className="text-gray-400 hover:text-brand-600 p-1.5 rounded-lg hover:bg-brand-50 transition-colors text-xs disabled:opacity-30"
                                          title={t("academy.builder.moveDown")}
                                        >
                                          <i className="fa-solid fa-arrow-down"></i>
                                        </button>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            removeLesson(si, li);
                                          }}
                                          className="text-gray-400 hover:text-red-500 p-1.5 rounded-lg hover:bg-red-50 transition-colors text-xs"
                                          title={t("academy.builder.deleteLesson")}
                                        >
                                          <i className="fa-solid fa-trash-can"></i>
                                        </button>
                                      </div>
                                    </div>

                                    {/* Video URL / source (video only) */}
                                    {lesson.type === "video" && (
                                      <div
                                        className="flex flex-col sm:flex-row gap-2 mt-2"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <input
                                          value={lesson.videoUrl || ""}
                                          onChange={(e) =>
                                            updateLesson(si, li, {
                                              videoUrl: e.target.value,
                                            })
                                          }
                                          className="flex-1 text-xs border border-gray-200 rounded-lg px-2 py-1.5 focus:ring-1 focus:ring-brand-500 outline-none"
                                          placeholder={t("academy.builder.videoUrlPlaceholder")}
                                        />
                                        <select
                                          value={lesson.source || "youtube"}
                                          onChange={(e) =>
                                            updateLesson(si, li, {
                                              source: e.target
                                                .value as LessonSource,
                                            })
                                          }
                                          className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-white focus:ring-1 focus:ring-brand-500 outline-none"
                                        >
                                          {sources.map((s) => (
                                            <option key={s} value={s}>
                                              {t(`webinars.sources.${s}`)}
                                            </option>
                                          ))}
                                        </select>
                                      </div>
                                    )}

                                    {/* Chips + resources */}
                                    <div className="flex flex-wrap items-center gap-2 mt-2">
                                      <span
                                        className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${chip.cls}`}
                                      >
                                        <i
                                          className={`fa-solid ${chip.icon} text-xs`}
                                        ></i>{" "}
                                        {t(`academy.lessonTypes.${lesson.type}`)}
                                      </span>
                                      {lesson.type === "video" && !thumb && (
                                        <span className="inline-flex items-center gap-1 text-xs text-amber-600 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">
                                          <i className="fa-solid fa-circle-exclamation text-xs"></i>{" "}
                                          {t("academy.builder.noVideo")}
                                        </span>
                                      )}

                                      {(lesson.resources || []).map((r, ri) => {
                                        const uploadKey = `${resUploadKeyPrefix}${ri}`;
                                        const isUploading =
                                          uploadingRes === uploadKey;
                                        return (
                                          <span
                                            key={ri}
                                            onClick={(e) => e.stopPropagation()}
                                            className="inline-flex items-center gap-1 bg-brand-50 border border-brand-200 text-brand-700 text-xs font-medium px-2 py-0.5 rounded-full"
                                          >
                                            {isUploading ? (
                                              <i className="fa-solid fa-spinner fa-spin text-xs"></i>
                                            ) : (
                                              <i
                                                className={`fa-solid ${
                                                  r.type === "file"
                                                    ? "fa-file-lines"
                                                    : "fa-link"
                                                } text-xs`}
                                              ></i>
                                            )}{" "}
                                            {r.title || r.url}
                                            <span
                                              onClick={() =>
                                                removeResource(si, li, ri)
                                              }
                                              className="ml-1 opacity-60 hover:opacity-100 cursor-pointer"
                                            >
                                              <i className="fa-solid fa-xmark text-xs"></i>
                                            </span>
                                          </span>
                                        );
                                      })}

                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openLinkModal(si, li);
                                        }}
                                        className="inline-flex items-center gap-1 text-xs text-brand-600 hover:text-brand-700 font-medium transition-colors"
                                      >
                                        <i className="fa-solid fa-link text-xs"></i>{" "}
                                        {t("academy.builder.addLink")}
                                      </button>
                                      <label
                                        onClick={(e) => e.stopPropagation()}
                                        className="inline-flex items-center gap-1 text-xs text-brand-600 hover:text-brand-700 font-medium transition-colors cursor-pointer"
                                      >
                                        <i className="fa-solid fa-plus text-xs"></i>{" "}
                                        {t("academy.builder.addFile")}
                                        <input
                                          type="file"
                                          className="hidden"
                                          onChange={(e) => {
                                            const f = e.target.files?.[0];
                                            if (f) addFileDirect(si, li, f);
                                            e.target.value = "";
                                          }}
                                        />
                                      </label>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}

                            <button
                              onClick={() => addLesson(si)}
                              className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-200 rounded-xl text-sm font-medium text-gray-500 hover:bg-brand-50 hover:border-brand-500 hover:text-brand-700 transition-all"
                            >
                              <i className="fa-solid fa-plus text-xs"></i>{" "}
                              {t("academy.builder.addLessonToSection", { number: si + 1 })}
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* Add new section */}
                  <button
                    onClick={addSection}
                    className="w-full flex items-center justify-center gap-3 px-6 py-4 border-2 border-dashed border-gray-300 rounded-xl text-sm font-semibold text-gray-600 hover:border-brand-400 hover:text-brand-600 hover:bg-brand-50/50 transition-all group"
                  >
                    <div className="w-7 h-7 rounded-full bg-gray-100 group-hover:bg-brand-100 flex items-center justify-center transition-colors">
                      <i className="fa-solid fa-plus text-gray-500 group-hover:text-brand-600 text-xs transition-colors"></i>
                    </div>
                    {t("academy.builder.addNewSection")}
                  </button>
                </div>

                {/* Right: Quick Settings — Release Schedule */}
                <div className="xl:col-span-1">
                  <div className="sticky top-6">
                    <div className="bg-white rounded-xl border-2 border-brand-200 shadow-sm overflow-hidden">
                      <div className="px-5 py-4 border-b border-brand-100 bg-brand-50">
                        <div className="flex items-center gap-2">
                          <i className="fa-solid fa-sliders text-brand-600 text-sm"></i>
                          <h3 className="text-sm font-bold text-gray-900">
                            {t("academy.builder.quickSettings")}
                          </h3>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          {t("academy.builder.scheduleHint")}
                        </p>
                      </div>

                      {sections.length === 0 ? (
                        <div className="p-8 text-center">
                          <i className="fa-regular fa-clock text-2xl text-gray-300 mb-2"></i>
                          <p className="text-xs text-gray-500">
                            {t("academy.builder.scheduleEmpty")}
                          </p>
                        </div>
                      ) : (
                        <div className="p-5">
                          <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-3">
                            {t("academy.builder.releaseSchedule")}
                          </label>
                          <div className="space-y-1">
                            {sections.map((section, si) => (
                              <SectionScheduleRow
                                key={section.id}
                                index={si}
                                title={
                                  section.title ||
                                  t("academy.builder.sectionNamePattern", { number: si + 1 })
                                }
                                schedule={section.schedule ?? null}
                                onChange={(sch) =>
                                  updateSectionSchedule(si, sch)
                                }
                              />
                            ))}
                          </div>
                          <p className="text-xs text-gray-400 mt-3">
                            {t("academy.builder.scheduleFootnote")}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}


          {/* Bottom nav */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-200">
            {step === 0 ? (
              <span />
            ) : (
              <button
                onClick={goBack}
                className="flex items-center gap-2 px-5 py-2.5 bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg text-sm font-medium shadow-sm"
              >
                <i className="fa-solid fa-arrow-left text-xs"></i>
                {t("academy.builder.back")}
              </button>
            )}
            <div className="flex items-center gap-3">
              <span className="text-xs text-gray-400 hidden sm:block">
                {t("academy.builder.stepIndicator", {
                  current: step + 1,
                  total: STEPS.length,
                })}
              </span>
              {step === 0 ? (
                <button
                  onClick={saveStep1AndNext}
                  disabled={isSaving}
                  className="flex items-center gap-2 px-6 py-2.5 bg-brand-600 text-white hover:bg-brand-700 rounded-lg text-sm font-semibold shadow-sm disabled:opacity-60"
                >
                  {isSaving ? t("common.loading.saving") : t("academy.builder.saveAndNext")}
                  <i className="fa-solid fa-arrow-right text-xs"></i>
                </button>
              ) : (
                <button
                  onClick={publish}
                  disabled={isSaving}
                  className="flex items-center gap-2 px-6 py-2.5 bg-brand-600 text-white hover:bg-brand-700 rounded-lg text-sm font-semibold shadow-sm disabled:opacity-60"
                >
                  <i className="fa-solid fa-bolt text-xs"></i>
                  {isSaving ? t("common.loading.publishing") : t("academy.builder.publish")}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Add Link modal */}
      {linkModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setLinkModal(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-xl w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
                <i className="fa-solid fa-link text-brand-600"></i>{" "}
                {t("academy.builder.addLinkModalTitle")}
              </h3>
              <button
                onClick={() => setLinkModal(null)}
                className="h-8 w-8 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 flex items-center justify-center"
              >
                <i className="fa-solid fa-xmark"></i>
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("academy.builder.linkTitleLabel")} <span className="text-red-500">*</span>
                </label>
                <input
                  autoFocus
                  value={linkTitle}
                  onChange={(e) => setLinkTitle(e.target.value)}
                  className={input}
                  placeholder={t("academy.builder.linkTitlePlaceholder")}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("academy.builder.linkUrlLabel")} <span className="text-red-500">*</span>
                </label>
                <input
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && saveLink()}
                  className={input}
                  placeholder="https://..."
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100">
              <button
                onClick={() => setLinkModal(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                {t("common.cancel")}
              </button>
              <button
                onClick={saveLink}
                disabled={!linkTitle.trim() || !linkUrl.trim()}
                className="px-5 py-2 text-sm font-semibold text-white bg-brand-600 rounded-lg hover:bg-brand-700 disabled:opacity-50"
              >
                {t("academy.builder.addLink")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ---- Small presentational helpers (Step 2 only) ----

const WEEKDAYS = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];

// Per-section release schedule editor: lock the section until a specific date,
// or release it on a recurring weekday (relative to each partner's enrollment).
// One compact row per section inside the single Release Schedule panel.
const SectionScheduleRow: React.FC<{
  index: number;
  title: string;
  schedule: SectionSchedule | null;
  onChange: (s: SectionSchedule | null) => void;
}> = ({ index, title, schedule, onChange }) => {
  const t = useT();
  const mode: "none" | "date" | "weekday" = !schedule ? "none" : schedule.type;

  const localValue = schedule?.date
    ? (() => {
        const d = new Date(schedule.date);
        if (isNaN(d.getTime())) return "";
        const pad = (n: number) => String(n).padStart(2, "0");
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
          d.getDate()
        )}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
      })()
    : "";

  return (
    <div className="flex flex-col gap-2 py-2 border-b border-gray-100 last:border-0">
      <div className="flex items-center gap-2 min-w-0">
        <span className="w-5 h-5 rounded bg-brand-600 text-white flex items-center justify-center text-[10px] font-bold flex-shrink-0">
          {index + 1}
        </span>
        <span className="text-sm font-medium text-gray-800 truncate">
          {title}
        </span>
      </div>

      <select
        value={mode}
        onChange={(e) => {
          const v = e.target.value as "none" | "date" | "weekday";
          if (v === "none") onChange(null);
          else if (v === "date")
            onChange({ type: "date", date: schedule?.date });
          else onChange({ type: "weekday", weekday: schedule?.weekday ?? 4 });
        }}
        className="px-2 py-1.5 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-brand-500 outline-none"
      >
        <option value="none">{t("academy.builder.scheduleOptions.none")}</option>
        <option value="date">{t("academy.builder.scheduleOptions.date")}</option>
        <option value="weekday">{t("academy.builder.scheduleOptions.weekday")}</option>
      </select>

      {mode === "date" && (
        <input
          type="datetime-local"
          value={localValue}
          onChange={(e) =>
            onChange({
              type: "date",
              date: e.target.value
                ? new Date(e.target.value).toISOString()
                : undefined,
            })
          }
          className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-brand-500 outline-none"
        />
      )}

      {mode === "weekday" && (
        <select
          value={schedule?.weekday ?? 4}
          onChange={(e) =>
            onChange({ type: "weekday", weekday: parseInt(e.target.value, 10) })
          }
          className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-brand-500 outline-none"
        >
          {WEEKDAYS.map((d, i) => (
            <option key={i} value={i}>
              {t("academy.builder.everyDay", { day: t(`academy.weekdays.${d}`) })}
            </option>
          ))}
        </select>
      )}
    </div>
  );
};

export default CourseBuilder;
